import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next'; // Import useTranslation hook
import {
    Zap,
    BarChart2,
    TrendingUp,
    Dumbbell,
    Clock,
    Calendar,
    ArrowLeft,
    Scale,
    Info
} from 'lucide-react';
import AITrainingAdvisor from '../ai/AITrainingAdvisor';
import StyledCard from '../ui/StyledCard';
import GradientButton from '../ui/GradientButton';
import { Link } from 'react-router-dom';
import TrainingAnalyzer from './TrainingAnalyzer';
import { MLMonitor } from './utils/MLMonitor';
import { debugLog } from '../../utils/logger';

/**
 * AITrainingDashboard - Pagina dedicata per visualizzare statistiche avanzate 
 * e suggerimenti di allenamento basati sull'intelligenza artificiale.
 */
const AITrainingDashboard = () => {
    const { isDark } = useTheme();
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation(); // Initialize translation function
    const api = getAuthenticatedApi();

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filteredWorkouts, setFilteredWorkouts] = useState([]);
    const [allSeries, setAllSeries] = useState([]);
    const [stats, setStats] = useState({
        totalWorkouts: 0,
        totalSets: 0,
        totalDuration: 0,
        averageWorkoutDuration: 0,
        mostCommonExercises: [],
        leastCommonExercises: [],
        workoutFrequency: 0,
        progress: {},
        muscleGroupAnalysis: {
            muscleGroups: []
        },
        suggestions: []
    });
    const [timeRange, setTimeRange] = useState('month'); // 'week', 'month', 'all'
    const [debugMode, setDebugMode] = useState(false);

    // Usa useCallback per memoizzare la funzione e risolvere l'avviso di dipendenza di useEffect
    const loadStatistics = useCallback(async () => {
      try {
          setLoading(true);
          setError(null);
  
          // Carica gli allenamenti
          const workoutsResponse = await api.get('/allenamenti.php');
          const allWorkouts = workoutsResponse.data.filter(w => w.durata_totale !== null);
  
          // Filtra gli allenamenti in base all'intervallo di tempo
          const currentFilteredWorkouts = TrainingAnalyzer.filterWorkoutsByTimeRange(allWorkouts, timeRange);
          setFilteredWorkouts(currentFilteredWorkouts);
  
          // Se non ci sono allenamenti nel periodo selezionato, imposta statistiche vuote ma con valori predefiniti
          if (currentFilteredWorkouts.length === 0) {
              setStats({
                  totalWorkouts: 0,
                  totalSets: 0,
                  totalDuration: 0,
                  averageWorkoutDuration: 0,
                  workoutFrequency: 0,
                  mostCommonExercises: [],
                  leastCommonExercises: [],
                  progress: {},
                  muscleGroupAnalysis: {
                      muscleGroups: []
                  },
                  suggestions: [],
                  mlStatus: null
              });
              setAllSeries([]);
              setLoading(false);
              return;
          }
  
          // Recupera le serie per tutti gli allenamenti filtrati
          const seriesPromises = currentFilteredWorkouts.map(async (workout) => {
              try {
                  const seriesResponse = await api.get(`/serie_completate.php?allenamento_id=${workout.id}`);
                  return seriesResponse.data.map(set => ({
                      ...set,
                      workout_id: workout.id,
                      data_allenamento: workout.data_allenamento
                  }));
              } catch (error) {
                  console.warn(`${t('aiAdvisor.errors.seriesLoad')} ${workout.id}:`, error);
                  return [];
              }
          });
  
          // Attendi il completamento di tutte le richieste di serie
          const seriesResults = await Promise.all(seriesPromises);
          const combinedSeries = seriesResults.flat();
          setAllSeries(combinedSeries);
  
          // Genera il rapporto di allenamento
          const report = await TrainingAnalyzer.generateTrainingReportWithML({
              workouts: currentFilteredWorkouts,
              sets: combinedSeries,
              timeRange,
              debug: process.env.NODE_ENV === 'development'
          });
  
          // Verifica lo stato ML solo in ambiente di sviluppo
          let mlStatus = null;
          if (process.env.NODE_ENV === 'development') {
              try {
                  mlStatus = await MLMonitor.checkMLStatus(currentFilteredWorkouts, combinedSeries);
              } catch (mlStatusError) {
                  console.warn(`${t('common.errors.connectionError', { message: mlStatusError })}`, mlStatusError);
              }
          }
  
          // CORREZIONE: Log per debug dei dati
          //debugLog("Allenamenti filtrati:", currentFilteredWorkouts.length);
          //debugLog("Serie combinate:", combinedSeries.length);
          //debugLog("Report generato:", report);
  
          // Calcola manualmente le statistiche base se mancherebbero nel report
          let workoutStats = report.workoutStats;
          if (!workoutStats) {
              workoutStats = {
                totalWorkouts: currentFilteredWorkouts.length,
                totalSets: combinedSeries.length || 0,
                totalDuration: 0,
                averageWorkoutDuration: 0,
                workoutFrequency: 0
              };
              
              // Calcola durata totale
              workoutStats.totalDuration = currentFilteredWorkouts.reduce(
                (sum, w) => sum + (parseInt(w.durata_totale) || 0), 0
              );
              
              // Calcola durata media
              if (workoutStats.totalWorkouts > 0) {
                  workoutStats.averageWorkoutDuration = 
                      Math.round(workoutStats.totalDuration / workoutStats.totalWorkouts);
              }
              
              // Calcola frequenza settimanale
              if (timeRange === 'week') {
                  workoutStats.workoutFrequency = workoutStats.totalWorkouts;
              } else if (timeRange === 'month') {
                  workoutStats.workoutFrequency = (workoutStats.totalWorkouts / 4).toFixed(1);
              } else {
                  // Calcola frequenza in base all'intervallo date
                  if (currentFilteredWorkouts.length >= 2) {
                      const sortedWorkouts = [...currentFilteredWorkouts].sort(
                          (a, b) => new Date(a.data_allenamento) - new Date(b.data_allenamento)
                      );
                      const firstDate = new Date(sortedWorkouts[0].data_allenamento);
                      const lastDate = new Date(sortedWorkouts[sortedWorkouts.length - 1].data_allenamento);
                      const weeksDiff = Math.max(1, Math.round((lastDate - firstDate) / (7 * 24 * 60 * 60 * 1000)));
                      workoutStats.workoutFrequency = (workoutStats.totalWorkouts / weeksDiff).toFixed(1);
                  } else {
                      workoutStats.workoutFrequency = workoutStats.totalWorkouts;
                  }
              }
          }

          // Forza il parsing numerico per i valori delle statistiche
          workoutStats.totalWorkouts = parseInt(workoutStats.totalWorkouts) || 0;
          workoutStats.totalSets = parseInt(workoutStats.totalSets) || 0;
          workoutStats.totalDuration = parseInt(workoutStats.totalDuration) || 0;
          workoutStats.averageWorkoutDuration = parseInt(workoutStats.averageWorkoutDuration) || 0;
          workoutStats.workoutFrequency = parseFloat(workoutStats.workoutFrequency) || 0;

          // Analizza manualmente la distribuzione degli esercizi se manca nel report
          let exerciseDistribution = report.exerciseDistribution;
          if (!exerciseDistribution || !exerciseDistribution.mostCommonExercises || !exerciseDistribution.leastCommonExercises) {
              exerciseDistribution = analyzeExerciseDistributionManually(combinedSeries);
          }

          // Calcola progresso esercizi manualmente se manca nel report
          let exerciseProgress = report.progress;
          if (!exerciseProgress || Object.keys(exerciseProgress).length === 0) {
              exerciseProgress = calculateExerciseProgressManually(combinedSeries);
          }
          
          // Aggiorna le statistiche
          setStats({
              ...report,
              workoutStats,
              progress: exerciseProgress || {},
              exerciseDistribution: exerciseDistribution,
              mostCommonExercises: exerciseDistribution.mostCommonExercises || [],
              leastCommonExercises: exerciseDistribution.leastCommonExercises || [],
              totalSets: combinedSeries.length,
              muscleGroupAnalysis: report.muscleGroupAnalysis || { muscleGroups: [] },
              mlStatus
          });
  
          setLoading(false);
      } catch (error) {
          console.error(`${t('aiAdvisor.errors.dataAnalysis')}:`, error);
          setError(t('aiAdvisor.errors.dataAnalysisMessage'));
          setLoading(false);
      }
  }, [api, timeRange, t]);

    // Funzione di fallback per analizzare la distribuzione degli esercizi
    function analyzeExerciseDistributionManually(sets) {
      // Crea un conteggio per nome di esercizio
      const exerciseCounts = {};
      sets.forEach(set => {
        const exerciseName = set.esercizio_nome;
        if (!exerciseName) return;
        
        exerciseCounts[exerciseName] = (exerciseCounts[exerciseName] || 0) + 1;
      });
      
      const sortedExercises = Object.entries(exerciseCounts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count);

      return {
        mostCommonExercises: sortedExercises.slice(0, 5),
        leastCommonExercises: sortedExercises.slice(-5).reverse(),
        totalExercises: sortedExercises.length
      };
    }

    // Funzione di fallback per calcolare il progresso degli esercizi
    function calculateExerciseProgressManually(sets) {
      // Raggruppa le serie per esercizio
      const exerciseMap = {};

      sets.forEach(set => {
        const exerciseId = set.esercizio_id || set.scheda_esercizio_id;
        const exerciseName = set.esercizio_nome;
        
        if (!exerciseId || !exerciseName) return;
        
        if (!exerciseMap[exerciseName]) {
          exerciseMap[exerciseName] = {
            sets: []
          };
        }
        
        exerciseMap[exerciseName].sets.push({
          date: new Date(set.timestamp || set.data_allenamento || set.data || new Date()),
          reps: parseInt(set.ripetizioni) || 0,
          weight: parseFloat(set.peso) || 0
        });
      });

      // Calcola i progressi per ogni esercizio
      const progressData = {};
      
      for (const [exerciseName, data] of Object.entries(exerciseMap)) {
        if (data.sets.length < 2) continue;
        
        // Ordina i set per data
        data.sets.sort((a, b) => a.date - b.date);
        
        // Raggruppa i set per data (sessione)
        const setsByWorkout = {};
        data.sets.forEach(set => {
          const dateStr = set.date.toISOString().substring(0, 10); // YYYY-MM-DD
          if (!setsByWorkout[dateStr]) {
            setsByWorkout[dateStr] = [];
          }
          setsByWorkout[dateStr].push(set);
        });
        
        // Ottieni date ordinate
        const workoutDates = Object.keys(setsByWorkout).sort();
        
        // Se non ci sono almeno 2 allenamenti, salta
        if (workoutDates.length < 2) continue;

        // Calcola statistiche per prima e ultima sessione
        const firstWorkoutSets = setsByWorkout[workoutDates[0]];
        const lastWorkoutSets = setsByWorkout[workoutDates[workoutDates.length - 1]];
        
        // Calcola peso massimo per prima e ultima sessione
        const firstMaxWeight = Math.max(...firstWorkoutSets.map(s => s.weight));
        const lastMaxWeight = Math.max(...lastWorkoutSets.map(s => s.weight));
        
        // Calcola cambiamento percentuale
        const weightChange = lastMaxWeight - firstMaxWeight;
        const weightChangePercent = firstMaxWeight > 0 
          ? (weightChange / firstMaxWeight) * 100 
          : 0;
        
        // Aggiungi ai dati di progresso solo se c'è stato un cambiamento
        if (Math.abs(weightChangePercent) > 0) {
          progressData[exerciseName] = {
            initialWeight: formatWeight(firstMaxWeight),
            currentWeight: formatWeight(lastMaxWeight),
            maxWeight: formatWeight(lastMaxWeight),
            weightChange: weightChange.toFixed(1),
            weightChangePercent: parseFloat(weightChangePercent.toFixed(1)),
            suggestedWeightRange: `${formatWeight(lastMaxWeight * 1.05)} - ${formatWeight(lastMaxWeight * 1.1)}`,
            lastWorkoutDetails: {
              date: workoutDates[workoutDates.length - 1],
              sets: lastWorkoutSets.length
            }
          };
        }
      }
      
      return progressData;
    }

    // Funzione di aiuto per formattare i pesi
    function formatWeight(weight) {
      return weight < 10 ? `${weight.toFixed(1)} kg` : `${weight.toFixed(0)} kg`;
    }

    // Carica statistiche all'inizializzazione del componente o quando cambia l'intervallo di tempo
    useEffect(() => {
        loadStatistics();
    }, [loadStatistics]);

    // Funzione per gestire il test ML
    const handleMLTest = React.useCallback(async () => {
        try {
            const testResults = await MLMonitor.testMLFunctionality(
                filteredWorkouts, 
                allSeries
            );
            //debugLog('Risultati test ML:', testResults);
            
            // Aggiungi una visualizzazione più dettagliata dei risultati
            const resultMessage = `
                ${t('aiAdvisor.completeAnalysis')}:
                - ${t('aiAdvisor.suggestions.standardSuggestions')}: ${testResults.standardSuggestions?.count || 0}
                - ${t('aiAdvisor.suggestions.personalizedSuggestions')}: ${testResults.personalizedSuggestions?.count || 0}
                - ${t('aiAdvisor.suggestions.mlSuggestions')}: ${testResults.mlSuggestions?.count || 0}
            `;
            
            alert(resultMessage);
        } catch (error) {
            console.error(`${t('common.error')} ML:`, error);
            alert(`${t('common.error')} ML: ${error.message}`);
        }
    }, [filteredWorkouts, allSeries, t]);

    /**
     * Renderizza la sezione di progresso degli esercizi
     * @returns {JSX.Element} Componente di progresso degli esercizi
     */
    const renderExerciseProgress = () => {
        const progressEntries = Object.entries(stats.progress || {});
        
        // Se non ci sono progressi da mostrare
        if (progressEntries.length === 0) {
            return (
                <div className="text-center p-6 bg-gray-800 dark:bg-gray-800 rounded-lg border border-gray-700 transition-colors">
                    <BarChart2 className="h-10 w-10 mx-auto mb-2 text-gray-500" />
                    <h4 className="font-medium text-gray-300 mb-1">{t('aiAdvisor.noSuggestions')}</h4>
                    <p className="text-gray-400 mb-3">
                        {t('aiAdvisor.suggestions.notEnoughData')}
                    </p>
                    <div className="p-4 rounded-lg bg-gray-900/50 border border-gray-700 text-sm">
                        <p className="text-gray-400 mb-2">
                            {t('aiAdvisor.suggestions.toViewProgress')}:
                        </p>
                        <ul className="text-gray-500 space-y-1 list-disc list-inside">
                            <li>{t('aiAdvisor.suggestions.completeWorkouts')}</li>
                            <li>{t('aiAdvisor.suggestions.recordWeight')}</li>
                            <li>{t('aiAdvisor.suggestions.useSameExercises')}</li>
                        </ul>
                    </div>
                </div>
            );
        }
        
        // Se ci sono pochi dati (meno di 3 esercizi con progresso), mostra un messaggio informativo
        if (progressEntries.length < 3) {
            return (
                <div>
                    <div className="p-4 mb-4 bg-blue-900/20 rounded-lg border border-blue-800 text-sm transition-colors">
                        <p className="text-blue-300 mb-2">
                            <Info className="h-4 w-4 inline mr-1" />
                            <span>{t('aiAdvisor.suggestions.initialAnalysis')}</span>
                        </p>
                        <p className="text-gray-400">
                            {t('aiAdvisor.suggestions.continueTraining')}
                        </p>
                    </div>
                    
                    {/* Mostra gli esercizi disponibili */}
                    <div className="space-y-4">
                        {progressEntries.map(([exerciseName, data]) => (
                            <div key={exerciseName} className="p-4 bg-gray-800 dark:bg-gray-800 rounded-lg border border-gray-700 transition-colors">
                                <div className="flex justify-between mb-2">
                                    <h3 className="font-medium text-white">{exerciseName}</h3>
                                    <div className={`flex items-center ${
                                        data.weightChangePercent > 0 
                                            ? 'text-green-400' 
                                            : data.weightChangePercent < 0 
                                                ? 'text-red-400'
                                                : 'text-gray-400'
                                    }`}>
                                        {data.weightChangePercent > 0 && <TrendingUp className="h-4 w-4 mr-1" />}
                                        {data.weightChangePercent < 0 && <TrendingUp className="h-4 w-4 mr-1 transform rotate-180" />}
                                        <span className="text-sm font-medium">
                                        {data.weightChangePercent > 0 ? '+' : ''}
                                        {data.weightChangePercent}%
                                    </span>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">{t('aiAdvisor.suggestions.estimatedRM')}:</span>
                                        <span className="font-medium text-white">{data.currentRM}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">{t('workout.weight')} {t('aiAdvisor.metrics.progressShort')}:</span>
                                        <span className="font-medium text-white">{data.maxWeight}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">{t('workout.weight')} {t('dashboard.stats.averageWorkout')}:</span>
                                        <span className="font-medium text-white">{data.averageWeight}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-400">{t('aiAdvisor.metrics.progress')} {t('aiAdvisor.metrics.weekly')}:</span>
                                        <span className="font-medium text-white">{data.weeklyProgressRate} kg</span>
                                    </div>
                                </div>
                                
                                {/* Box di dettaglio */}
                                <div className="mt-3 p-3 bg-gray-700/50 rounded text-xs border border-gray-600">
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <span className="text-gray-400">1RM {t('aiAdvisor.metrics.initial')}:</span>
                                            <span className="font-medium text-white float-right">{data.initialRM}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">1RM {t('aiAdvisor.metrics.current')}:</span>
                                            <span className="font-medium text-white float-right">{data.currentRM}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">{t('aiAdvisor.metrics.period')}:</span>
                                            <span className="font-medium text-white float-right">{data.timePeriod} {t('aiAdvisor.metrics.weeks')}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">{t('aiAdvisor.metrics.lastWorkout')}:</span>
                                            <span className="font-medium text-white float-right">{data.lastWorkoutDetails?.date}</span>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Barra di progresso visiva */}
                                <div className="mt-3 h-2 bg-gray-700 rounded-full overflow-hidden">
                                    <div 
                                        className={`h-full rounded-full ${
                                            data.weightChangePercent > 0 
                                                ? 'bg-green-500' 
                                                : 'bg-red-500'
                                        }`}
                                        style={{ width: `${Math.min(100, Math.abs(data.weightChangePercent))}%` }}
                                    ></div>
                                </div>
                                
                                {/* Suggerimento per il prossimo peso target */}
                                <div className="mt-3 p-2 bg-gray-700 rounded text-xs">
                                    <span className="text-gray-300">{t('aiAdvisor.suggestions.nextTarget')}: </span>
                                    <span className="text-blue-300 font-medium">{data.suggestedWeightRange}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
    
        // Visualizzazione per quando ci sono molti dati
        return (
            <div className="space-y-4">
                {progressEntries.slice(0, 5).map(([exerciseName, data]) => (
                    <div key={exerciseName} className="p-4 bg-gray-800 dark:bg-gray-800 rounded-lg border border-gray-700 transition-colors">
                        <div className="flex justify-between mb-2">
                            <h3 className="font-medium text-white flex items-center">
                                <Scale className="h-4 w-4 mr-2 text-gray-400" />
                                {exerciseName}
                            </h3>
                            <div className={`flex items-center ${
                                parseFloat(data.weightChangePercent) > 0 
                                    ? 'text-green-400' 
                                    : parseFloat(data.weightChangePercent) < 0 
                                        ? 'text-red-400'
                                        : 'text-gray-400'
                            }`}>
                                {parseFloat(data.weightChangePercent) > 0 && <TrendingUp className="h-4 w-4 mr-1" />}
                                {parseFloat(data.weightChangePercent) < 0 && <TrendingUp className="h-4 w-4 mr-1 transform rotate-180" />}
                                <span className="text-sm font-medium">
                                    {data.weightChangePercent > 0 ? '+' : ''}
                                    {data.weightChangePercent}%
                                </span>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-400">{t('aiAdvisor.metrics.initialWeight')}:</span>
                                <span className="font-medium text-white">{data.initialWeight}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">{t('aiAdvisor.metrics.currentWeight')}:</span>
                                <span className="font-medium text-white">{data.currentWeight}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">{t('aiAdvisor.metrics.weeklyProgress')}:</span>
                                <span className="font-medium text-white">{data.weeklyProgressRate} kg</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">{t('aiAdvisor.metrics.period')}:</span>
                                <span className="font-medium text-white">{data.timePeriod} {t('aiAdvisor.metrics.weeks')}</span>
                            </div>
                        </div>

                        {/* Barra di progresso */}
                        <div className="mt-3 h-2 bg-gray-700 rounded-full overflow-hidden">
                            <div 
                                className={`h-full rounded-full ${
                                    data.weightChangePercent > 0 
                                        ? 'bg-green-500' 
                                        : 'bg-red-500'
                                }`}
                                style={{ width: `${Math.min(100, Math.abs(data.weightChangePercent))}%` }}
                            ></div>
                        </div>
                        
                        {/* Suggerimento per il prossimo peso target */}
                        <div className="mt-3 p-2 bg-gray-700 rounded text-xs">
                            <span className="text-gray-300">{t('aiAdvisor.suggestions.nextTarget')}: </span>
                            <span className="text-blue-300 font-medium">{data.suggestedWeightRange}</span>
                        </div>
                    </div>
                ))}
            </div>
        );
    };
    
    /**
     * Renderizza l'analisi dei gruppi muscolari
     * @returns {JSX.Element} Componente di analisi dei gruppi muscolari
     */
    const renderMuscleGroupAnalysis = () => {
        // Controlla che stats.muscleGroupAnalysis esista prima di destrutturare
        const muscleGroups = stats.muscleGroupAnalysis?.muscleGroups || [];
        
        if (!stats.muscleGroupAnalysis || !muscleGroups || muscleGroups.length === 0) {
            return (
                <div className="text-center p-4 text-gray-400">
                    <p>{t('aiAdvisor.suggestions.notEnoughDataMuscle')}</p>
                </div>
            );
        }
        
        // Calcola il valore massimo per la normalizzazione delle barre
        const maxCount = Math.max(...muscleGroups.map(mg => mg.count));
        
        return (
            <div className="space-y-3">
                <h3 className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-3`}>
                    {t('aiAdvisor.muscleGroupDistribution')}
                </h3>
                
                {muscleGroups.map((mg, index) => (
                    <div key={index} className="mb-2">
                        <div className="flex justify-between mb-1">
                            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                {mg.group}
                            </span>
                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {mg.count} {t('workout.sets').toLowerCase()}
                            </span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                            <div 
                                className={`h-full rounded-full ${
                                    index === 0 ? 'bg-blue-500' :
                                    index === 1 ? 'bg-green-500' :
                                    index === 2 ? 'bg-purple-500' :
                                    index === 3 ? 'bg-yellow-500' :
                                    'bg-gray-500'
                                }`}
                                style={{ width: `${(mg.count / maxCount) * 100}%` }}
                            ></div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Funzione per formattare la durata
    const formatDuration = (duration) => {
      // Assicurati che duration sia un numero
      const minutes = parseInt(duration) || 0;
      
      if (minutes < 60) {
        return `${minutes}m`;
      }
      
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      
      if (remainingMinutes === 0) {
        return `${hours}h`;
      }
      
      return `${hours}h ${remainingMinutes}m`;
    };

    return (
        <div className={`max-w-full w-full ${isDark ? 'bg-gray-900' : 'bg-gray-100'} min-h-screen p-4 transition-colors duration-200`}>
          {/* Header con titolo e pulsante di ritorno */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2 transition-colors">
              <Zap className={`h-6 w-6 ${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors`} />
              <span>{t('aiAdvisor.trainingTitle')}</span>
            </h1>
            
            <Link to="/dashboard">
              <GradientButton
                variant="secondary"
                size="default"
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {t('userProfile.backToDashboard')}
              </GradientButton>
            </Link>
          </div>
      
          {/* Filtro periodo temporale */}
          <div className="mb-6">
            <div className="inline-flex rounded-md shadow-sm" role="group">
              <button
                onClick={() => setTimeRange('week')}
                className={`px-4 py-2 text-sm font-medium rounded-l-lg border transition-colors ${
                  timeRange === 'week'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : `${isDark ? 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700' : 
                                'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`
                }`}
              >
                {t('aiAdvisor.timeRange.lastWeek')}
              </button>
              <button
                onClick={() => setTimeRange('month')}
                className={`px-4 py-2 text-sm font-medium border-t border-b transition-colors ${
                  timeRange === 'month'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : `${isDark ? 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700' : 
                                'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`
                }`}
              >
                {t('aiAdvisor.timeRange.lastMonth')}
              </button>
              <button
                onClick={() => setTimeRange('all')}
                className={`px-4 py-2 text-sm font-medium rounded-r-lg border transition-colors ${
                  timeRange === 'all'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : `${isDark ? 'bg-gray-800 text-gray-300 border-gray-600 hover:bg-gray-700' : 
                                'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`
                }`}
              >
                {t('aiAdvisor.timeRange.all')}
              </button>
            </div>
          </div>
      
          {/* Visualizzazione errore */}
          {error ? (
            <StyledCard variant="danger">
              <StyledCard.Header>
                <StyledCard.Title>{t('aiAdvisor.errorTitle')}</StyledCard.Title>
              </StyledCard.Header>
              <StyledCard.Body>
                <p>{error}</p>
                <GradientButton 
                  onClick={loadStatistics}
                  className="mt-4"
                >
                  {t('aiAdvisor.retryButton')}
                </GradientButton>
              </StyledCard.Body>
            </StyledCard>
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Statistiche sommarie */}
              <div className="lg:col-span-2">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Allenamenti */}
                  <StyledCard hover={true} className="flex items-center p-4 transition-colors">
                    <div className={`p-3 mr-4 ${isDark ? 'bg-blue-900' : 'bg-blue-100'} rounded-full transition-colors`}>
                      <Dumbbell className={`h-6 w-6 ${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} transition-colors`}>
                        {t('dashboard.stats.workouts')}
                      </p>
                      <p className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'} transition-colors`}>
                        {loading ? (
                          <span className={`inline-block w-6 h-6 animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded transition-colors`}></span>
                        ) : (
                          stats.workoutStats?.totalWorkouts || 0
                        )}
                      </p>
                    </div>
                  </StyledCard>
      
                  {/* Serie */}
                  <StyledCard hover={true} className="flex items-center p-4 transition-colors">
                    <div className={`p-3 mr-4 ${isDark ? 'bg-green-900' : 'bg-green-100'} rounded-full transition-colors`}>
                      <BarChart2 className={`h-6 w-6 ${isDark ? 'text-green-400' : 'text-green-600'} transition-colors`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} transition-colors`}>
                        {t('workout.sets')}
                      </p>
                      <p className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'} transition-colors`}>
                        {loading ? (
                          <span className={`inline-block w-6 h-6 animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded transition-colors`}></span>
                        ) : (
                          stats.totalSets || 0
                        )}
                      </p>
                    </div>
                  </StyledCard>
      
                  {/* Durata Media */}
                  <StyledCard hover={true} className="flex items-center p-4 transition-colors">
                    <div className={`p-3 mr-4 ${isDark ? 'bg-purple-900' : 'bg-purple-100'} rounded-full transition-colors`}>
                      <Clock className={`h-6 w-6 ${isDark ? 'text-purple-400' : 'text-purple-600'} transition-colors`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} transition-colors`}>
                        {t('aiAdvisor.metrics.averageDuration')}
                      </p>
                      <p className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'} transition-colors`}>
                        {loading ? (
                          <span className={`inline-block w-6 h-6 animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded transition-colors`}></span>
                        ) : (
                          formatDuration(stats.workoutStats?.averageWorkoutDuration || 0)
                        )}
                      </p>
                    </div>
                  </StyledCard>
      
                  {/* Frequenza */}
                  <StyledCard hover={true} className="flex items-center p-4 transition-colors">
                    <div className={`p-3 mr-4 ${isDark ? 'bg-amber-900' : 'bg-amber-100'} rounded-full transition-colors`}>
                      <Calendar className={`h-6 w-6 ${isDark ? 'text-amber-400' : 'text-amber-600'} transition-colors`} />
                    </div>
                    <div>
                      <p className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'} transition-colors`}>
                        {t('aiAdvisor.metrics.frequency')}
                      </p>
                      <p className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'} transition-colors`}>
                        {loading ? (
                          <span className={`inline-block w-6 h-6 animate-pulse ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded transition-colors`}></span>
                        ) : (
                          `${stats.workoutStats?.workoutFrequency || 0}/${t('aiAdvisor.metrics.perWeek')}`
                        )}
                      </p>
                    </div>
                  </StyledCard>
                </div>
              </div>
      
              {/* Consulente AI - Versione completa */}
              <div className="lg:col-span-2">
                <AITrainingAdvisor />
              </div>
      
              {/* Progressi Esercizi */}
              <StyledCard className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} transition-colors`}>
                <StyledCard.Header>
                  <StyledCard.Title>
                    <div className="flex items-center gap-2">
                      <TrendingUp className={`h-5 w-5 ${isDark ? 'text-green-400' : 'text-green-600'} transition-colors`} />
                      {t('aiAdvisor.metrics.exerciseProgress')}
                    </div>
                  </StyledCard.Title>
                  <StyledCard.Subtitle>
                    {t('aiAdvisor.metrics.exerciseProgressSubtitle')}
                  </StyledCard.Subtitle>
                </StyledCard.Header>
                <StyledCard.Body>
                  {loading ? (
                    <div className="animate-pulse space-y-4">
                      {[...Array(3)].map((_, i) => (
                        <div key={i} className={`h-20 ${isDark ?'bg-gray-700' : 'bg-gray-200'} rounded-lg transition-colors`}></div>
                      ))}
                    </div>
                  ) : (
                    renderExerciseProgress()
                  )}
                </StyledCard.Body>
              </StyledCard>
      
              {/* Analisi Esercizi e Gruppi Muscolari */}
              <StyledCard className="transition-colors">
                <StyledCard.Header>
                  <StyledCard.Title>
                    <div className="flex items-center gap-2">
                      <BarChart2 className={`h-5 w-5 ${isDark ? 'text-blue-400' : 'text-blue-600'} transition-colors`} />
                      {t('aiAdvisor.muscleGroupAnalysis')}
                    </div>
                  </StyledCard.Title>
                  <StyledCard.Subtitle>
                    {t('aiAdvisor.exerciseDistribution')}
                  </StyledCard.Subtitle>
                </StyledCard.Header>
                <StyledCard.Body>
                  {loading ? (
                    <div className="animate-pulse space-y-4">
                      <div className={`h-40 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded-lg transition-colors`}></div>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Analisi Gruppi Muscolari */}
                      <div className="mb-6">
                        {renderMuscleGroupAnalysis()}
                      </div>
                    
                      {/* Esercizi più frequenti */}
                      <div>
                        <h3 className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-3 transition-colors`}>
                          {t('aiAdvisor.mostFrequentExercises')}
                        </h3>
                        {stats.mostCommonExercises && stats.mostCommonExercises.length > 0 ? (
                          <div className="space-y-2">
                            {stats.mostCommonExercises.map((exercise, index) => (
                              <div key={index} className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <div className={`w-6 h-6 flex items-center justify-center ${isDark ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800'} rounded-full mr-2 text-xs font-medium transition-colors`}>
                                    {index + 1}
                                  </div>
                                  <span className={`${isDark ? 'text-gray-200' : 'text-gray-800'} transition-colors`}>
                                    {exercise.name}
                                  </span>
                                </div>
                                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} transition-colors`}>
                                  {exercise.count} {t('workout.sets').toLowerCase()}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm transition-colors`}>
                            {t('aiAdvisor.suggestions.insufficientData')}
                          </p>
                        )}
                      </div>
      
                      {/* Esercizi meno frequenti */}
                      <div>
                        <h3 className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-3 transition-colors`}>
                          {t('aiAdvisor.leastFrequentExercises')}
                        </h3>
                        {stats.leastCommonExercises && stats.leastCommonExercises.length > 0 ? (
                          <div className="space-y-2">
                            {stats.leastCommonExercises.map((exercise, index) => (
                              <div key={index} className="flex items-center justify-between">
                                <div className="flex items-center">
                                  <div className={`w-6 h-6 flex items-center justify-center ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'} rounded-full mr-2 text-xs font-medium transition-colors`}>
                                    {index + 1}
                                  </div>
                                  <span className={`${isDark ? 'text-gray-200' : 'text-gray-800'} transition-colors`}>
                                    {exercise.name}
                                  </span>
                                </div>
                                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'} transition-colors`}>
                                  {exercise.count} {t('workout.sets').toLowerCase()}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm transition-colors`}>
                            {t('aiAdvisor.suggestions.insufficientData')}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </StyledCard.Body>
              </StyledCard>
              
              {/* Sezione ML Status (solo in ambiente di sviluppo) */}
              {process.env.NODE_ENV === 'development' && stats.mlStatus && (
                <StyledCard className={`transition-colors mt-6 ${isDark ? 'bg-gray-800/80 border-purple-700/50' : 'bg-gray-50/80 border-purple-300/50'}`}>
                  <StyledCard.Header>
                    <StyledCard.Title>
                      <div className="flex items-center gap-2">
                        <Zap className={`h-5 w-5 ${isDark ? 'text-purple-400' : 'text-purple-600'} transition-colors`} />
                        {t('aiAdvisor.mlStatus')}
                      </div>
                    </StyledCard.Title>
                  </StyledCard.Header>
                  <StyledCard.Body>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-white'} border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                        <div className="text-sm text-gray-400">{t('aiAdvisor.mlStatusLabel')}</div>
                        <div className={`font-medium ${stats.mlStatus.isActive ? 'text-green-400' : 'text-red-400'}`}>
                          {stats.mlStatus.isActive ? t('aiAdvisor.active') : t('aiAdvisor.inactive')}
                        </div>
                      </div>
                      <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-white'} border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                        <div className="text-sm text-gray-400">{t('aiAdvisor.dataQuality')}</div>
                        <div className="font-medium text-blue-400">
                          {stats.mlStatus.dataQuality}
                        </div>
                      </div>
                      <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-white'} border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                        <div className="text-sm text-gray-400">{t('aiAdvisor.estimatedAccuracy')}</div>
                        <div className="font-medium text-purple-400">
                          {stats.mlStatus.estimatedAccuracy}
                        </div>
                      </div>
                      <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-white'} border ${isDark ? 'border-gray-600' : 'border-gray-200'}`}>
                        <div className="text-sm text-gray-400">{t('aiAdvisor.availableModels')}</div>
                        <div className="font-medium text-amber-400">
                          {stats.mlStatus.availableModels?.length || 0}
                        </div>
                      </div>
                    </div>
                    <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-white'} border ${isDark ? 'border-gray-600' : 'border-gray-200'} text-xs`}>
                      <div className="mb-2 text-gray-400 font-medium">{t('aiAdvisor.generatedFeatures')}</div>
                      <div className="grid grid-cols-3 gap-2">
                        {stats.mlStatus.featuresGenerated && Object.entries(stats.mlStatus.featuresGenerated).map(([key, value]) => (
                          <div key={key} className="flex justify-between">
                            <span className="text-gray-400">{key}:</span>
                            <span className="text-blue-400">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="mt-4 text-right">
                      <button
                        onClick={handleMLTest}
                        className={`text-xs px-3 py-1 ${isDark ? 'bg-purple-700 text-purple-100 hover:bg-purple-600' : 'bg-purple-100 text-purple-800 hover:bg-purple-200'} rounded transition-colors`}
                      >
                        {t('aiAdvisor.runMLTest')}
                      </button>
                    </div>
                  </StyledCard.Body>
                </StyledCard>
              )}
            </div>
          )}
        </div>
    );
};

export default AITrainingDashboard;